import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";
import Plot from "react-plotly.js";
import { toast } from "react-toastify";

import { supportGetClientVersions } from "src/apiService/modules/support";
import type {
  ClientVersionBucket,
  ClientVersionStats,
} from "src/interfaces/clientVersionStats.interface";
import SupportDateRangeFilter from "src/pages/support/SupportDateRangeFilter";
import {
  DayMs,
  rangeForSupportDatePreset,
  type SupportDateRangePresetId,
} from "src/pages/support/dateRangePresets";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken, supportIsAdmin } from "src/store/support/selector";

const PLOT_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
];

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
  },
  chartWrapper: {
    width: "100%",
  },
  chart: {
    width: "100%",
    minHeight: 420,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

function formatBucketLabel(bucket: ClientVersionBucket, bucketMs: number) {
  const start = new Date(bucket.start);
  if (bucketMs <= DayMs) return format(start, "MMM d, HH:mm");
  return format(start, "MMM d");
}

function buildVersionChartTraces(stats: ClientVersionStats) {
  const x = stats.buckets.map((bucket) =>
    formatBucketLabel(bucket, stats.bucketMs)
  );

  const bucketTotals = stats.buckets.map((_, i) =>
    stats.versions.reduce((sum, v) => sum + (stats.counts[v]?.[i] ?? 0), 0)
  );

  return stats.versions.map((version, index) => {
    const rawCounts = stats.counts[version] ?? [];
    const y = rawCounts.map((count, i) =>
      bucketTotals[i] > 0 ? count / bucketTotals[i] : 0
    );
    const color = PLOT_COLORS[index % PLOT_COLORS.length];

    return {
      x,
      y,
      customdata: rawCounts,
      name: version,
      type: "scatter" as const,
      mode: "lines" as const,
      stackgroup: "one",
      line: { color, width: 0.5 },
      fillcolor: color,
      fill: (index === 0 ? "tozeroy" : "tonexty") as "tozeroy" | "tonexty",
      hovertemplate:
        "%{fullData.name}<br />%{x}<br />%{y:.1%} (%{customdata} clients)<extra></extra>",
    };
  });
}

function ClientVersions() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);
  const isAdmin = useSelector(supportIsAdmin);

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<ClientVersionStats | undefined>();
  const [activePreset, setActivePreset] =
    useState<SupportDateRangePresetId | null>("1d");
  const [from, setFrom] = useState(() => new Date(Date.now() - DayMs));
  const [to, setTo] = useState(() => new Date());

  const fetchStats = useCallback(
    async (fromDate: Date, toDate: Date) => {
      if (!token) return;

      setLoading(true);
      try {
        const { data } = await supportGetClientVersions(
          token,
          fromDate.toISOString(),
          toDate.toISOString()
        );
        setStats(data);
      } catch (err: any) {
        if (err?.code === "failed-precondition") {
          dispatch(setSupportLoginToken());
        }
        setStats(undefined);
        toast.error(err?.message || "Failed to load client versions");
      } finally {
        setLoading(false);
      }
    },
    [dispatch, token]
  );

  const applyPreset = useCallback(
    (presetId: SupportDateRangePresetId, fromDate: Date, toDate: Date) => {
      setActivePreset(presetId);
      setFrom(fromDate);
      setTo(toDate);
      void fetchStats(fromDate, toDate);
    },
    [fetchStats]
  );

  useEffect(() => {
    if (!isAdmin || !token) return;
    const range = rangeForSupportDatePreset("1d");
    applyPreset("1d", range.from, range.to);
  }, [isAdmin, token, applyPreset]);

  const plot = useMemo(() => {
    if (!stats?.versions.length || !stats.buckets.length) return null;

    const traces = buildVersionChartTraces(stats);
    const denseXLabels = stats.buckets.length > 12;

    return (
      <Box className={classes.chartWrapper}>
        <Plot
          className={classes.chart}
          useResizeHandler
          style={{ width: "100%", height: "100%" }}
          data={traces}
          revision={stats.buckets.length}
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: 420,
            width: undefined,
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: {
              color: theme.palette.text.primary,
            },
            margin: {
              l: 48,
              r: 16,
              t: 56,
              b: denseXLabels ? 88 : 64,
            },
            xaxis: {
              title: "Time",
              tickangle: denseXLabels ? -40 : 0,
              automargin: true,
              nticks: denseXLabels
                ? Math.min(12, stats.buckets.length)
                : undefined,
            },
            yaxis: {
              title: "Active clients (%)",
              tickformat: ".0%",
              range: [0, 1],
            },
            legend: {
              orientation: "h",
              yanchor: "bottom",
              y: 1.02,
              x: 0,
              xanchor: "left",
            },
            showlegend: true,
          }}
        />
      </Box>
    );
  }, [stats, classes.chart, classes.chartWrapper, theme.palette.text.primary]);

  if (!isAdmin) {
    return (
      <Box m={2} component={Paper} className={classes.paper}>
        <Typography color="textSecondary">
          Admin access is required to view client version analytics.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component={Paper} className={classes.paper}>
      <Typography variant="h6" gutterBottom>
        Client Version
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Each point shows the share of browsers on each build as of that time
        (latest version-check per browser).
      </Typography>

      <SupportDateRangeFilter
        from={from}
        to={to}
        activePreset={activePreset}
        onPresetChange={applyPreset}
        onFromChange={setFrom}
        onToChange={setTo}
        onApply={() => {
          setActivePreset(null);
          void fetchStats(from, to);
        }}
      />

      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      ) : plot ? (
        plot
      ) : (
        <Typography color="textSecondary">
          No version-check events in this range.
        </Typography>
      )}
    </Box>
  );
}

export default ClientVersions;
